import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LoadingWindowAssignment,
  HeatQualityFlagDetail,
  HeatQualityIssue,
  HeatSummary,
} from "src/store/api/generatedApi";
import { HeatQualityFlagRows } from "components/heatQualityIssues/HeatQualityFlagRows";
import { HeatPlannedMaterials } from "./HeatPlannedMaterials";

dayjs.extend(utc);

const WINDOW_TIME_FORMAT = "HH:mm:ss";

export const HeatLoadingWindows = ({
  loadingWindows,
  heats,
  viewingTime,
  qualityIssues = [],
}: {
  loadingWindows: LoadingWindowAssignment[];
  heats: HeatSummary[];
  viewingTime: Dayjs;
  qualityIssues?: HeatQualityIssue[];
}) => {
  const { t } = useTenantTranslation();
  const firstActiveRef = useRef<HTMLDivElement>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(
    () => new Set()
  );

  const orderedWindows = useMemo(
    () =>
      sorted(loadingWindows, (assignment) =>
        dayjs.utc(assignment.start).valueOf()
      ),
    [loadingWindows]
  );

  const plannedMaterialsByHeat = useMemo(
    () =>
      new Map(
        heats.map((heat): [string, string[] | null] => [
          heat.heat_id,
          heat.planned_material_names ?? null,
        ])
      ),
    [heats]
  );

  const flagsByHeat = useMemo(
    () =>
      new Map<string, HeatQualityFlagDetail[]>(
        qualityIssues
          .filter((issue) => issue.flags.length > 0)
          .map((issue) => [issue.heat_id, issue.flags])
      ),
    [qualityIssues]
  );

  const toggleRow = (rowKey: string) => {
    setExpandedRows((previous) => {
      const next = new Set(previous);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const viewingTimeMs = viewingTime.valueOf();
  const isActive = (assignment: LoadingWindowAssignment) =>
    dayjs.utc(assignment.start).valueOf() <= viewingTimeMs &&
    viewingTimeMs < dayjs.utc(assignment.end).valueOf();
  const firstActiveIndex = orderedWindows.findIndex(isActive);

  useEffect(() => {
    firstActiveRef.current?.scrollIntoView({ block: "center" });
  }, [firstActiveIndex]);

  return (
    <Stack spacing={0.5}>
      {firstActiveIndex === -1 ? (
        <Typography variant="caption" color="text.secondary">
          {t("notCurrentlyLoading")}
        </Typography>
      ) : null}
      {orderedWindows.map((assignment, index) => {
        const active = isActive(assignment);
        const rowKey = `${assignment.bucket_id}-${assignment.start}`;
        const flags = flagsByHeat.get(assignment.heat_id) ?? [];
        const hasIssues = flags.length > 0;
        const isExpanded = expandedRows.has(rowKey);
        return (
          <Box
            key={rowKey}
            ref={index === firstActiveIndex ? firstActiveRef : undefined}
          >
            <Box
              sx={{
                p: 0.75,
                borderRadius: 0.5,
                border: "1px solid",
                borderColor: active ? "primary.main" : "grey.300",
                bgcolor: active ? "action.selected" : "transparent",
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: active ? "fontWeightBold" : "fontWeightRegular",
                  }}
                >
                  {assignment.heat_id}
                  {assignment.basket_number !== null
                    ? ` · ${t("basket")} ${assignment.basket_number}`
                    : ""}
                </Typography>
                {hasIssues ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      toggleRow(rowKey);
                    }}
                    aria-label={t("heatQualityIssues")}
                    sx={{ p: 0.25, flexShrink: 0 }}
                  >
                    <WarningAmberRoundedIcon fontSize="small" color="warning" />
                  </IconButton>
                ) : null}
              </Stack>
              <Typography variant="body2Mono" color="text.secondary">
                {dayjs.utc(assignment.start).format(WINDOW_TIME_FORMAT)}–
                {dayjs.utc(assignment.end).format(WINDOW_TIME_FORMAT)} UTC
              </Typography>
              {hasIssues ? (
                <Collapse in={isExpanded} unmountOnExit>
                  <Box sx={{ mt: 0.5 }}>
                    <HeatQualityFlagRows flags={flags} showDetails={false} />
                  </Box>
                </Collapse>
              ) : null}
            </Box>
            {active ? (
              <Box sx={{ mt: 0.5, pl: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("plannedRecipe")}
                </Typography>
                <HeatPlannedMaterials
                  plannedMaterialNames={
                    plannedMaterialsByHeat.get(assignment.heat_id) ?? null
                  }
                />
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Stack>
  );
};
